import { useState, useEffect, useCallback } from 'react'
import { Modal } from '../ui/Modal'
import { Avatar } from '../ui/Avatar'
import { Spinner } from '../ui/Spinner'
import { usersApi } from '../../api/users'
import { chatsApi } from '../../api/chats'
import { groupsApi } from '../../api/groups'
import { useChatStore } from '../../stores/chatStore'
import { useUiStore } from '../../stores/uiStore'
import type { User } from '../../types'

interface NewChatModalProps {
  onClose: () => void
  onCreateGroup: () => void
  onCreateChannel: () => void
  onBotStore: () => void
}

export function NewChatModal({ onClose, onCreateGroup, onCreateChannel, onBotStore }: NewChatModalProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [openingId, setOpeningId] = useState<string | null>(null)
  const [inviteLink, setInviteLink] = useState('')
  const [joining, setJoining] = useState(false)
  const { upsertChat, setActiveChat } = useChatStore()
  const { addToast } = useUiStore()

  const search = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return }
    setLoading(true)
    try {
      const res = await usersApi.search(q)
      setResults(res.users ?? [])
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => search(query), 300)
    return () => clearTimeout(t)
  }, [query, search])

  const handleJoinByLink = async () => {
    const link = inviteLink.trim()
    if (!link) return
    // Extract code from URL or use as-is
    const codeMatch = link.match(/\/join\/([^/?\s]+)/) ?? link.match(/\+([A-Za-z0-9_-]+)/) ?? [null, link]
    const code = codeMatch[1] ?? link
    setJoining(true)
    try {
      const r = await groupsApi.joinViaInvite(code)
      const chat = await chatsApi.getOrCreate(r.group_id).catch(async () => {
        // group_id is a group, not user — need to open differently
        return { id: r.group_id, type: 'group' as const, title: 'Group', members: [], created_at: '', last_message: null, unread_count: 0, is_pinned: false, is_muted: false, folder: 'all' }
      })
      upsertChat(chat)
      setActiveChat(chat.id)
      onClose()
    } catch {
      addToast('error', 'Неверная ссылка или вы уже участник')
    } finally {
      setJoining(false)
    }
  }

  const openChat = async (user: User) => {
    setOpeningId(user.id)
    try {
      const chat = await chatsApi.getOrCreate(user.id)
      upsertChat({ ...chat, peer: user })
      setActiveChat(chat.id)
      onClose()
    } catch {
      addToast('error', 'Не удалось открыть чат')
    } finally {
      setOpeningId(null)
    }
  }

  return (
    <Modal onClose={onClose} title="Новый чат">
      <div className="p-3 space-y-2">
        {/* Create group shortcut */}
        <button
          onClick={() => { onClose(); onCreateGroup() }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-bg-hover transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ea6f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <span className="text-txt-primary font-medium text-sm">Создать группу</span>
        </button>

        {/* Create channel shortcut */}
        <button
          onClick={() => { onClose(); onCreateChannel() }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-bg-hover transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ea6f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 14a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 10.91"/>
              <path d="M17 3l2 2-8 8"/>
            </svg>
          </div>
          <span className="text-txt-primary font-medium text-sm">Создать канал</span>
        </button>

        {/* Bot store shortcut */}
        <button
          onClick={() => { onClose(); onBotStore() }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-bg-hover transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ea6f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
            </svg>
          </div>
          <span className="text-txt-primary font-medium text-sm">Магазин ботов</span>
        </button>

        <div className="border-t border-divider" />

        {/* Join by invite link */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Ссылка-приглашение или код..."
            value={inviteLink}
            onChange={(e) => setInviteLink(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleJoinByLink()}
            className="flex-1 bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
          />
          <button
            onClick={handleJoinByLink}
            disabled={joining || !inviteLink.trim()}
            className="bg-accent text-white text-sm px-3 py-2 rounded-xl disabled:opacity-40 hover:bg-blue-500 transition-colors shrink-0"
          >
            {joining ? '…' : 'Войти'}
          </button>
        </div>

        <div className="border-t border-divider" />

        {/* Search */}
        <input
          type="text"
          placeholder="Поиск по имени, @username или телефону..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2.5 text-sm
                     outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
        />

        {/* Results */}
        {loading && (
          <div className="flex justify-center py-6"><Spinner /></div>
        )}

        {!loading && query.length >= 2 && results.length === 0 && (
          <p className="text-txt-secondary text-sm text-center py-6">Пользователи не найдены</p>
        )}

        {!loading && query.length < 2 && (
          <p className="text-txt-meta text-xs text-center py-4">Введите минимум 2 символа для поиска</p>
        )}

        <div className="space-y-0.5">
          {results.map((user) => (
            <button
              key={user.id}
              onClick={() => openChat(user)}
              disabled={openingId === user.id}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-bg-hover
                         transition-colors text-left disabled:opacity-60"
            >
              <Avatar
                name={user.first_name || user.username || '?'}
                src={user.avatar_url}
                size={40}
                online={user.online}
              />
              <div className="flex-1 min-w-0">
                <p className="text-txt-primary text-sm font-medium truncate">
                  {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.username}
                </p>
                {user.username && (
                  <p className="text-txt-secondary text-xs truncate">@{user.username}</p>
                )}
              </div>
              {openingId === user.id && <Spinner size={16} />}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  )
}
